/**
 * Hook: fetch top opportunities per tracked keyword for weekly Global Opportunities review.
 * Ranking runs on the server (POST /api/opportunities/top-per-keyword), same path MCP uses.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryEntities } from '@tracekit/core';
import { postTopPerKeyword, postWeeklyReviewSummaries } from '../api/opportunities';
import { resolveRecommendedKeywords } from '../utils/recommendedKeywordsResolver';
import { useGeographicZone } from '../contexts/GeographicZoneContext';
import { getGeographicZoneFilterValue } from '../utils/countryAccess';
import {
  WEEKLY_GLOBAL_OPPORTUNITIES_STALE_MS,
  TOP_OPPORTUNITIES_PER_KEYWORD,
  buildFallbackKeywordSummary,
  buildFallbackPageSummary,
  buildRecentlyExpiredMissed,
  buildWeeklyDigestHighlights,
  extractWeeklyReviewKeywords,
  filterWeeklyEntitiesByGeographicZone,
  getWeeklyReviewWeekLabel,
  mapSearchEntityToOpportunityEntity,
  mergeTrackingOwnerOntoEntities,
  normalizeRecommendedKeywordsResponse,
  trackingOwnerIndexFromEntities,
  type KeywordOpportunitySection,
  type WeeklyOpportunitySearchEntity,
} from '../utils/weeklyGlobalOpportunities';

export interface MonitorKeyword {
  keyword: string;
  confidencePct?: number;
}

export interface UseWeeklyGlobalOpportunitiesOptions {
  /** Saved user selection; empty array falls back to all recommended keywords. */
  trackedKeywords?: string[];
}

function resolveMonitorKeywords(
  keywordsData: ReturnType<typeof normalizeRecommendedKeywordsResponse>,
  trackedKeywords?: string[]
): MonitorKeyword[] {
  const normalized = normalizeRecommendedKeywordsResponse(keywordsData);
  const keywords =
    trackedKeywords && trackedKeywords.length > 0
      ? trackedKeywords
      : extractWeeklyReviewKeywords(normalized);

  return keywords.map((keyword) => {
    const match = normalized?.keywords?.find(
      (entry) => entry.keyword.toLowerCase() === keyword.toLowerCase()
    );
    return { keyword, confidencePct: match?.confidence_pct };
  });
}

export interface KeywordOpportunityQueryResult {
  opportunities: WeeklyOpportunitySearchEntity[];
  searchPool: WeeklyOpportunitySearchEntity[];
}

export function useWeeklyGlobalOpportunities(
  projectId: string | undefined,
  options?: UseWeeklyGlobalOpportunitiesOptions
) {
  const { selectedGeographicZone, isGeographicZoneReady } = useGeographicZone();
  const trackedKeywords = options?.trackedKeywords;
  const zoneFilter = getGeographicZoneFilterValue(selectedGeographicZone);

  const keywordsQuery = useQuery({
    queryKey: ['recommended-keywords', projectId],
    queryFn: async () =>
      normalizeRecommendedKeywordsResponse(await resolveRecommendedKeywords(projectId!)),
    enabled: !!projectId,
    staleTime: WEEKLY_GLOBAL_OPPORTUNITIES_STALE_MS,
  });

  const monitorKeywords = useMemo(
    () => resolveMonitorKeywords(keywordsQuery.data, trackedKeywords),
    [keywordsQuery.data, trackedKeywords]
  );

  const trackingOwnersQuery = useQuery({
    queryKey: ['weekly-tracking-owners', projectId],
    queryFn: async () => {
      const result = await queryEntities({
        project_id: projectId!,
        entity_type: 'govt_opportunity',
        filters: { pipeline_tracked: true },
        limit: 400,
        offset: 0,
      });
      return trackingOwnerIndexFromEntities(result.entities || []);
    },
    enabled: !!projectId,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const trackingById = trackingOwnersQuery.data;
  const keywordKey = monitorKeywords.map((entry) => entry.keyword).join('|');

  const topQuery = useQuery({
    queryKey: ['weekly-top-per-keyword', projectId, keywordKey, zoneFilter],
    queryFn: () =>
      postTopPerKeyword({
        project_id: projectId!,
        keywords: monitorKeywords.map((entry) => entry.keyword),
        limit: TOP_OPPORTUNITIES_PER_KEYWORD,
        geographic_zone: zoneFilter,
      }),
    enabled:
      !!projectId &&
      isGeographicZoneReady !== false &&
      monitorKeywords.length > 0,
    staleTime: WEEKLY_GLOBAL_OPPORTUNITIES_STALE_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const mergedByKeyword = useMemo(
    () =>
      monitorKeywords.map(({ keyword }) => {
        const row = topQuery.data?.results?.find(
          (entry) => entry.keyword.toLowerCase() === keyword.toLowerCase()
        );
        const raw = (row?.opportunities ?? []) as WeeklyOpportunitySearchEntity[];
        const zoned = filterWeeklyEntitiesByGeographicZone(raw, selectedGeographicZone);
        const withTracking = mergeTrackingOwnerOntoEntities(zoned, trackingById ?? new Map());
        return {
          keyword,
          opportunities: withTracking,
          searchPool: withTracking,
          error: row?.error ? String(row.error) : null,
        };
      }),
    [monitorKeywords, topQuery.data, selectedGeographicZone, trackingById]
  );

  const boardPending = !topQuery.isFetched || topQuery.isLoading;
  const boardError = topQuery.error ? String(topQuery.error) : null;

  const baseSections: KeywordOpportunitySection[] = useMemo(
    () =>
      monitorKeywords.map(({ keyword, confidencePct }, index) => ({
        keyword,
        confidencePct,
        opportunities: boardPending
          ? []
          : (mergedByKeyword[index]?.opportunities ?? []).map(mapSearchEntityToOpportunityEntity),
        isLoading: boardPending,
        isFetching: topQuery.isFetching,
        error: boardPending ? null : mergedByKeyword[index]?.error || boardError,
      })),
    [monitorKeywords, boardPending, mergedByKeyword, topQuery.isFetching, boardError]
  );

  const someSectionsReady =
    !keywordsQuery.isLoading && monitorKeywords.length > 0 && topQuery.isFetched && !topQuery.isLoading;

  const opportunitiesReady = someSectionsReady && !keywordsQuery.isFetching;

  const summariesQuery = useQuery({
    queryKey: [
      'weekly-review-summaries',
      projectId,
      selectedGeographicZone,
      keywordKey,
      baseSections
        .filter((section) => !section.isLoading)
        .map((s) => `${s.keyword}:${s.opportunities.map((o) => o.id).join(',')}`)
        .join(';'),
    ],
    queryFn: async () => {
      const payload = {
        project_id: projectId!,
        week_label: getWeeklyReviewWeekLabel(),
        sections: baseSections
          .filter((section) => !section.isLoading)
          .map((s) => ({
            keyword: s.keyword,
            opportunities: s.opportunities.slice(0, TOP_OPPORTUNITIES_PER_KEYWORD).map((o) => ({
              id: o.id,
              title: o.data?.title ?? '',
              organization: o.data?.organization ?? '',
              deadline: o.data?.deadline ?? '',
              match_score: o.data?.match_score,
              weekly_relevance_score: o.data?.weekly_relevance_score,
            })),
          })),
      };
      try {
        return await postWeeklyReviewSummaries(payload);
      } catch {
        return null;
      }
    },
    enabled: !!projectId && someSectionsReady,
    staleTime: WEEKLY_GLOBAL_OPPORTUNITIES_STALE_MS,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const sections: KeywordOpportunitySection[] = useMemo(
    () =>
      baseSections.map((section) => {
        const sectionPending = section.isLoading;
        const fromApi = summariesQuery.data?.sections?.find(
          (s) => s.keyword.toLowerCase() === section.keyword.toLowerCase()
        )?.summary;
        return {
          ...section,
          summary: sectionPending
            ? null
            : fromApi ||
              (!summariesQuery.isLoading
                ? buildFallbackKeywordSummary(section.keyword, section.opportunities)
                : null),
          summaryLoading:
            sectionPending ||
            (someSectionsReady && (summariesQuery.isLoading || summariesQuery.isFetching) && !fromApi),
        };
      }),
    [
      baseSections,
      someSectionsReady,
      summariesQuery.data,
      summariesQuery.isLoading,
      summariesQuery.isFetching,
    ]
  );

  const highlights = useMemo(() => {
    const readySections = sections.filter((section) => !section.isLoading);
    if (readySections.length === 0) {
      return {
        topPicks: [],
        newThisWeek: [],
        expiringSoon: [],
        recentlyExpiredMissed: [],
      };
    }

    const digest = buildWeeklyDigestHighlights(readySections);
    const keywordPools = monitorKeywords.map(({ keyword }, index) => ({
      keyword,
      entities: boardPending ? [] : mergedByKeyword[index]?.searchPool ?? [],
    }));
    const recentlyExpiredMissed = buildRecentlyExpiredMissed(keywordPools);

    return { ...digest, recentlyExpiredMissed };
  }, [sections, monitorKeywords, boardPending, mergedByKeyword]);

  const pageSummary = useMemo(() => {
    if (!someSectionsReady) return null;
    if (summariesQuery.data?.page_summary) return summariesQuery.data.page_summary;
    if (summariesQuery.isLoading || summariesQuery.isFetching) return null;
    return buildFallbackPageSummary(sections.filter((section) => !section.isLoading), highlights);
  }, [
    someSectionsReady,
    summariesQuery.data,
    summariesQuery.isLoading,
    summariesQuery.isFetching,
    sections,
    highlights,
  ]);

  const isLoadingKeywords = keywordsQuery.isLoading || keywordsQuery.isFetching;
  const isLoadingOpportunities =
    isLoadingKeywords || (monitorKeywords.length > 0 && (!topQuery.isFetched || topQuery.isLoading));
  const isFetching = keywordsQuery.isFetching || topQuery.isFetching || summariesQuery.isFetching;

  const searchPoolEntities = useMemo(() => {
    const merged: WeeklyOpportunitySearchEntity[] = [];
    const seen = new Set<string>();
    for (const row of mergedByKeyword) {
      for (const entity of row.searchPool) {
        const id = String(entity.id);
        if (seen.has(id)) continue;
        seen.add(id);
        merged.push(entity);
      }
    }
    return merged;
  }, [mergedByKeyword]);

  const refetch = async () => {
    await keywordsQuery.refetch();
    await topQuery.refetch();
    await summariesQuery.refetch();
  };

  return {
    sections,
    highlights,
    pageSummary,
    keywords: monitorKeywords,
    searchPoolEntities,
    isLoading: isLoadingOpportunities,
    isFetching,
    opportunitiesReady,
    keywordsError: keywordsQuery.error,
    refetch,
    weekLabel: getWeeklyReviewWeekLabel(),
    selectedGeographicZone,
  };
}
